import { useHtmlDir } from '@aboutyou/legacy-shared/src/LegacyWebCoreConfigContext'
import { Locale } from '@aboutyou/legacy-shared/src/types'
import { useTranslation } from 'helpers/hooks/useTranslation'
import * as R from 'ramda'
import React from 'react'
import { useLocaleStore } from 'store/context'
import styled from 'styled-components'
import Button from 'uiComponents/atoms/Buttons/Button'
import ColorBubble from 'uiComponents/atoms/ColorBubble/ColorBubble'
import { darkGrayColor } from 'uiComponents/constants/colors'
import {
  fontFamilyPrimary,
  fsXXS,
  fsXXXS,
  fwBold,
  fwMediumBold,
} from 'uiComponents/constants/typography'
import LowestProductPrice30D from '../LowestProductPrice30D/LowestProductPrice30D'
import PriceBox from '../PriceBox/PriceBox'

const MAXIMUM_COLORS = 4
export const CTAHeight = 60

export const CTAButton = styled(Button)`
  width: calc(100% - 16px);
  opacity: 0;
  margin-top: 8px;
  transition: opacity 250ms ease-in-out;
  position: absolute;
  border-radius: 4px;
  left: 8px;
`

export const ContentWrapper = styled.div`
  padding: 12px 8px;
  margin-bottom: -16px;
  line-height: 1.4em;
  background: inherit;
  z-index: 1;
  background-color: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(10px);
`

const BrandName = styled.p`
  font-size: ${fsXXXS}px;
  ${fwBold};
  text-transform: uppercase;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  text-align: center;
`

const ProductName = styled.p`
  font-size: ${fsXXXS}px;
  color: ${darkGrayColor};
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  text-align: center;
`

export const ColorContainer = styled.ul`
  display: flex;
  gap: 5px;
  align-items: center;
  direction: ltr;
  transition: opacity 300ms ease-in, transform 300ms ease-in;
`

const ColorRemainder = styled.li`
  ${fontFamilyPrimary};
  color: ${darkGrayColor};
  font-size: ${fsXXXS}px;
  line-height: ${fsXXS}px;
`

export const Sizes = styled.span`
  ${fontFamilyPrimary};
  font-size: ${fsXXXS}px;
  color: ${darkGrayColor};
  ${fwMediumBold};
  white-space: pre-wrap;
  position: absolute;
  opacity: 0;
  transform: translateY(5px);
  transition: opacity 300ms ease-in, transform 300ms ease-in;
  top: -5px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  width: 100%;
  text-align: center;
  direction: ltr;
`

const StyledPriceBox = styled(PriceBox)`
  display: flex;
  justify-content: center;
  text-align: center;
  flex-wrap: wrap;
  margin-top: 2px;
  margin-bottom: 10px;
`

const DetailsWrapper = styled.div`
  display: flex;
  justify-content: center;
  position: relative;
  height: 15px;
`

const StyledLowestProductPrice30D = styled(LowestProductPrice30D)`
  padding-top: 8px;
  text-align: center;
  justify-content: center;
`

interface IProductColors {
  code: string
  isLightColor: boolean
}

interface IProductVariant {
  value: string
}

export interface ProductTileContentProps {
  name: string
  productName: string
  formattedBasePrice: string | undefined
  enableProductName: boolean
  formattedSalePrice?: string
  formattedCampaignPrice?: string
  secondaryLabelBasePrice?: string
  secondaryLabelSalePrice?: string
  secondaryLabelCampaignPrice?: string
  lowestPriorPrice?: string
  colorsList?: IProductColors[]
  variantsList?: IProductVariant[]
  hasCTA?: boolean
  CTAContent?: React.ReactNode
  onCTAClick?: () => void
  href?: string
  enableAlternativesButton?: boolean
  isMarketplaceProduct?: boolean
  isSecondHandProduct?: boolean
  enablePriceDisplayOverhaul?: boolean
  isAggregatedProduct?: boolean
}

const shouldHideSizePreview = (locale: Locale) =>
  Boolean([Locale.it_IT, Locale.es_ES, Locale.fr_FR].includes(locale))

const getVariantValues = R.pluck('value')

const VariantList: React.FC<{ items: string[] }> = ({ items }) => {
  const [t] = useTranslation()
  const isRTL = useHtmlDir() === 'rtl'
  const isLongVariantList =
    items && (items.length > 6 || (items.length > 4 && items.includes('-')))

  if (isLongVariantList) {
    return <>{t('in vielen Größen verfügbar')}</>
  }
  const variantsList = isRTL ? items.reverse() : items

  return (
    <>
      {t('Verfügbare Größen')}: {variantsList.join(', ')}
    </>
  )
}

const ProductTileContent: React.FC<ProductTileContentProps> = ({
  name,
  productName,
  formattedBasePrice,
  formattedSalePrice,
  formattedCampaignPrice,
  lowestPriorPrice,
  colorsList = [],
  variantsList,
  enableProductName,
  hasCTA,
  CTAContent,
  onCTAClick,
  secondaryLabelBasePrice,
  secondaryLabelSalePrice,
  secondaryLabelCampaignPrice,
  isMarketplaceProduct,
  isSecondHandProduct,
  enablePriceDisplayOverhaul,
  isAggregatedProduct = false,
}) => {
  const locale = useLocaleStore(store => store.locale)
  const colors = colorsList.slice(0, MAXIMUM_COLORS)
  const remainderColorsLength = colorsList.slice(MAXIMUM_COLORS).length

  const ctaClickHandler = React.useCallback(
    (ev: React.MouseEvent) => {
      ev.preventDefault()
      ev.stopPropagation()
      if (onCTAClick) {
        onCTAClick()
      }
    },
    [onCTAClick],
  )

  const showColorList = colorsList.length > 0
  const showSizesList = variantsList && !shouldHideSizePreview(locale)
  const showDetailsWrapper = showColorList || showSizesList
  const isNotMarketPlaceAndSecondHand =
    !isMarketplaceProduct && !isSecondHandProduct
  const hasAnyReduction = formattedCampaignPrice || formattedSalePrice
  const displayRrpPrefix =
    enablePriceDisplayOverhaul &&
    (isNotMarketPlaceAndSecondHand ||
      (isAggregatedProduct && !isMarketplaceProduct))
  const hideLowestProductPrice30D =
    enablePriceDisplayOverhaul &&
    ((isNotMarketPlaceAndSecondHand && hasAnyReduction) ||
      (isAggregatedProduct && !isMarketplaceProduct))
  const hideDoubleStrikePrice = Boolean(
    enablePriceDisplayOverhaul && formattedCampaignPrice && formattedSalePrice,
  )

  return (
    <ContentWrapper>
      <BrandName data-testid="BrandName">{name}</BrandName>
      {enableProductName && (
        <ProductName data-testid="ProductName">{productName}</ProductName>
      )}
      <StyledPriceBox
        formattedBasePrice={formattedBasePrice}
        formattedSalePrice={formattedSalePrice}
        formattedCampaignPrice={formattedCampaignPrice}
        secondaryLabelBasePrice={secondaryLabelBasePrice}
        secondaryLabelSalePrice={secondaryLabelSalePrice}
        secondaryLabelCampaignPrice={secondaryLabelCampaignPrice}
        displayRrpPrefix={displayRrpPrefix}
        hideDoubleStrikePrice={hideDoubleStrikePrice}
      />

      {showDetailsWrapper && (
        <DetailsWrapper>
          {showColorList && (
            <ColorContainer data-testid="ColorContainer">
              {colors.map(({ code, isLightColor }, index) => (
                <ColorBubble
                  data-testid="ColorBubble"
                  as="li"
                  key={index}
                  color={code}
                  isLightColor={isLightColor}
                />
              ))}
              {remainderColorsLength > 0 && (
                <ColorRemainder>+{remainderColorsLength}</ColorRemainder>
              )}
            </ColorContainer>
          )}
          {showSizesList && (
            <Sizes data-testid="Sizes">
              <VariantList items={getVariantValues(variantsList)} />
            </Sizes>
          )}
        </DetailsWrapper>
      )}
      {!hideLowestProductPrice30D && (
        <StyledLowestProductPrice30D label={lowestPriorPrice} />
      )}
      {hasCTA && (
        <CTAButton data-testid="ProductTileCTA" onClick={ctaClickHandler}>
          {CTAContent}
        </CTAButton>
      )}
    </ContentWrapper>
  )
}

export default ProductTileContent
